import React, { useState, useEffect } from "react";
import {
  Container,
  TextField,
  Button,
  InputAdornment,
  Table,
  Box,
  Modal,
  TableBody,
  TableCell,
  CircularProgress,
  TableHead,
  AppBar,
  Toolbar,
  Paper,
  TableRow,
  IconButton,
  Typography,
  Grid,
  Tooltip,
  Divider,
  Chip
} from "@mui/material";
import { Delete as DeleteIcon, Edit as EditIcon } from '@mui/icons-material';
import SearchIcon from "@mui/icons-material/Search";
import InfoIcon from "@mui/icons-material/Info";
import { useNavigate, useParams } from "react-router-dom";
import base64 from "base64-js";
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

// Info modal field descriptions
const infoSections = [
  {
    title: "Page Overview",
    color: "#0B1F3D",
    description:
      "The Fine Details page allows authorized users to view, add, edit, and delete fine records associated with fee types and due dates. Access to each action is controlled by role-based permissions.",
  },
  {
    title: "Table Columns",
    color: "#1565C0",
    fields: [
      {
        label: "Fee Type",
        key: "chargeType",
        desc: "The category of fee (e.g., Tuition Fee, Transport Fee) for which the fine is applicable.",
      },
      {
        label: "Fine For",
        key: "monthName",
        desc: "The month or period for which the fine has been defined. Also used as the search filter.",
      },
      {
        label: "Start Date",
        key: "startDate",
        desc: "The date from which the fine period begins. Fines are applicable on or after this date.",
      },
      {
        label: "End Date",
        key: "endDate",
        desc: "The date on which the fine period ends. No fine is charged after this date.",
      },
      {
        label: "Fine Per Day",
        key: "finePerDay",
        desc: "The monetary amount charged as a fine for each day of delay within the defined date range.",
      },
      {
        label: "Action",
        key: "action",
        desc: "Contains Edit and Delete buttons. Edit navigates to the fine editing form. Delete opens a confirmation dialog before removing the record.",
      },
    ],
  },
  {
    title: "Permissions",
    color: "#2E7D32",
    fields: [
      {
        label: "Add (uWrite)",
        desc: "Allows creating new fine records. The ADD button is enabled only if this permission is granted.",
      },
      {
        label: "Edit (uModify)",
        desc: "Allows editing existing fine records. The EDIT button is enabled only if this permission is granted.",
      },
      {
        label: "Delete (uDelete)",
        desc: "Allows deleting fine records. The DELETE button is enabled only if this permission is granted.",
      },
      {
        label: "View (uRead)",
        desc: "Allows fetching and viewing the fine details list. Data is loaded only when this permission is active.",
      },
    ],
  },
  {
    title: "Search & Pagination",
    color: "#6A1B9A",
    fields: [
      {
        label: "Search",
        desc: "Filters fine records by the 'Fine For' (monthName) field in real time. Case-insensitive matching.",
      },
      {
        label: "Rows per Page",
        desc: "Controls how many records are displayed per page. Options: 5, 10, 25, 50.",
      },
      {
        label: "Pagination",
        desc: "Navigate between pages using the Prev / Next buttons. Page count updates automatically based on search results.",
      },
    ],
  },
];

function DueDateDetails() {
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [isLoading, setIsLoading] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const rowsPerPageOptions = [5, 10, 25, 50];
  const [fineDetails, setFineDetails] = useState([]);
  const [selectedFineDetails, setSelectedFineDetails] = useState({ dueDateId: null, monthName: '' });
  const [searchTerm, setSearchTerm] = useState("");
  const [loadingBarProgress, setLoadingBarProgress] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showInfoModal, setShowInfoModal] = useState(false); // State for Info Modal
  const [authorization, setAuthorization] = useState(null);
  const navigate = useNavigate();
  const { encodedFormId } = useParams();

  const decodeFormId = (encodedFormId) => {
    if (!encodedFormId || typeof encodedFormId !== "string") {
      console.error("encodedFormId is missing or invalid:", encodedFormId);
      return null;
    }
    try {
      const paddedEncodedFormId = encodedFormId.padEnd(Math.ceil(encodedFormId.length / 4) * 4, "=");
      const bytes = base64.toByteArray(paddedEncodedFormId);
      return new TextDecoder().decode(bytes);
    } catch (error) {
      console.error("Error decoding formId:", error);
      return null;
    }
  };

  const formId = decodeFormId(encodedFormId);

  const fetchFineDetails = async () => {
    try {
      setIsLoading(true); // Start loading
      const apiUrl = process.env.REACT_APP_BASE_URL;
      const token = sessionStorage.getItem("token");
      const response = await fetch(`${apiUrl}/Fine/GetFine`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
        body: JSON.stringify({}),
      });
      const data = await response.json();
      if (data.data === null && data.msg === "Record Not Found") {
        console.error('Record Not Found');
        // alert('Record Not Found');
        setSnackbarMessage('Record Not Found');
        setOpenSnackbar(true);
        setSnackbarSeverity("error");

        return;
      }
      setFineDetails(data);
    } catch (error) {
      console.error("API request error:", error);
      // alert("An error occurred. Please try again later.");
      setSnackbarMessage('An error occurred. Please try again later.');
      setOpenSnackbar(true);
      setSnackbarSeverity("error");
    } finally {
      setIsLoading(false); // Stop loading
    }
  };


  const Authorizer = async () => {
    if (!formId) {
      console.error("formId is null, cannot call Authorizer");
      setAuthorization({ isPermission: 0 });
      return;
    }
    try {
      const apiUrl = process.env.REACT_APP_BASE_URL;
      const token = sessionStorage.getItem("token");
      const response = await fetch(`${apiUrl}/CPanel/Module_Authorizer`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
        body: JSON.stringify({
          formId,
        }),
      });
      if (response.ok) {
        const responseData = await response.json();
        setAuthorization(responseData[0]);
      } else {
        console.error("Authorization failed");
        // alert("Authorization failed");
        setSnackbarMessage('Authorization failed');
        setOpenSnackbar(true);
        setSnackbarSeverity("error");
        setAuthorization({ isPermission: 0 });
      }
    } catch (error) {
      console.error("API request error:", error);
      // alert("An error occurred. Please try again later.");
      setSnackbarMessage('An error occurred. Please try again later.');
      setOpenSnackbar(true);
      setSnackbarSeverity("error");
      setAuthorization({ isPermission: 0 });
    }
  };

  const handleShow = (dueDateId, monthName) => {
    if (authorization?.uDelete === 1) {
      setSelectedFineDetails({ dueDateId, monthName });
      setShowModal(true);
    } else {
      // alert("You are not authorized to delete");
      setSnackbarMessage('You are not authorized to delete');
      setOpenSnackbar(true);
      setSnackbarSeverity("warning");
    }
  };

  const handleClose = () => {
    setSelectedFineDetails({ dueDateId: null, monthName: '' });
    setShowModal(false);
  };

  const handleEdit = (fineId) => {
    if (authorization?.uModify === 1) {
      navigate(`/editfine/${fineId}`);
    } else {
      // alert("You are not authorized to edit");
      setSnackbarMessage('You are not authorized to edit');
      setOpenSnackbar(true);
      setSnackbarSeverity("warning");
    }
  };

  const handleDelete = async (fineId) => {
    if (authorization?.uDelete !== 1) {
      // alert("You are not authorized to delete");
      setSnackbarMessage('You are not authorized to delete');
      setOpenSnackbar(true);
      setSnackbarSeverity("warning");
      return;
    }
    try {
      const apiUrl = process.env.REACT_APP_BASE_URL;
      setLoadingBarProgress(30);
      const token = sessionStorage.getItem("token");
      const response = await fetch(`${apiUrl}/Fine/Id?Id=${fineId}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
      });
      if (response.ok) {
        setShowModal(false);
        setLoadingBarProgress(100);
        setSnackbarMessage('Fine deleted successfully!');
        setOpenSnackbar(true);
        fetchFineDetails();
      } else {
        console.error("Delete failed");
        setLoadingBarProgress(0);
        // alert("Failed to delete fine");
        setSnackbarMessage('Failed to delete fine');
        setOpenSnackbar(true);
        setSnackbarSeverity("error");
      }
    } catch (error) {
      console.error("API request error:", error);
      // alert("An error occurred. Please try again later.");
      setSnackbarMessage('An error occurred. Please try again later.');
      setOpenSnackbar(true);
      setSnackbarSeverity("error");
    }
  };

  useEffect(() => {
    if (formId) {
      Authorizer();
      if (authorization?.uRead === 1) {
        fetchFineDetails();
      }
    } else {
      setAuthorization({ isPermission: 0 });
    }
  }, [formId, authorization?.uRead]);

  useEffect(() => {
    // Filter fine details based on search term
    // You can implement this logic if needed
  }, [searchTerm, fineDetails]);

  if (!authorization) {
    return <div>Loading...</div>;
  }

  if (authorization.isPermission !== 1 || !formId) {
    return <div>You are not authorized to view this page</div>;
  }

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(1); // reset to page 1
  };

  const filteredData = fineDetails
    .filter((fine) => fine.monthName.toLowerCase().includes(searchTerm.toLowerCase()));

  const paginatedData = filteredData.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage
  );



  return (
    <Container>
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12}></Grid>
        <AppBar position="static" style={{ backgroundColor: "#0B1F3D" }}>
          <Toolbar>
            <Typography variant="h4" component="div" style={{ fontWeight: 'bold', flexGrow: 1 }}>
              Fine Details
            </Typography>
            {/* ── Info Button in AppBar ── */}
            <Tooltip title="View page information & field details" arrow>
              <IconButton
                onClick={() => setShowInfoModal(true)}
                sx={{
                  color: '#fff',
                  backgroundColor: 'rgba(255,255,255,0.15)',
                  border: '1px solid rgba(255,255,255,0.4)',
                  borderRadius: '50%',
                  width: 38,
                  height: 38,
                  '&:hover': {
                    backgroundColor: 'rgba(255,255,255,0.28)',
                  },
                }}
              >
                <InfoIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Toolbar>
        </AppBar>
        <Paper elevation={3} style={{ padding: 16, width: '100%', margin: 'auto', marginTop: 6 }}>
          <div style={{ display: 'flex', justifyContent: "space-between", alignItems: 'center', margin: "10px auto" }}>
            <TextField
              type="text"
              id="search"
              placeholder="Search"
              style={{ width: "300px" }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton>
                      <SearchIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <Tooltip title={authorization.uWrite === 1 ? "" : "You are not authorized to add"} arrow>
              <span>
                <Button
                  variant="contained"
                  color="primary"
                  style={{
                    width: "120px",
                    height: "50px", // match TextField height
                    padding: "10px 0", // vertical padding similar to TextField
                  }}
                  onClick={() => navigate('/addfine')}
                  disabled={authorization.uWrite !== 1}
                >
                  <b>ADD</b>
                </Button>

              </span>
            </Tooltip>
          </div>

          <Grid item xs={12}>
            <Table
              sx={{
                minWidth: 650,
                border: '1px solid #ccc',
                '& .MuiTableCell-root': {
                  borderRight: '1px solid #ccc',
                  fontSize: '1rem',
                },
                '& .MuiTableHead-root .MuiTableCell-root': {
                  fontWeight: 'bold',
                  backgroundColor: '#f5f5f5',
                },
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell style={{ textAlign: 'center' }}><strong>Fee Type</strong></TableCell>
                  <TableCell style={{ textAlign: 'center' }}><strong>Fine For</strong></TableCell>
                  <TableCell style={{ textAlign: 'center' }}><strong>Start Date</strong></TableCell>
                  <TableCell style={{ textAlign: 'center' }}><strong>End Date</strong></TableCell>
                  <TableCell style={{ textAlign: 'center' }}><strong>Fine Per Day Date</strong></TableCell>
                  <TableCell style={{ textAlign: 'center' }}><strong>Action</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : paginatedData.length > 0 ? (
                  paginatedData.map((fine) => (
                    <TableRow key={fine.dueDateId}>
                      <TableCell style={{ textAlign: 'center' }}>{fine.chargeType}</TableCell>
                      <TableCell style={{ textAlign: 'center' }}>{fine.monthName}</TableCell>
                      <TableCell style={{ textAlign: 'center' }}>{fine.startDate}</TableCell>
                      <TableCell style={{ textAlign: 'center' }}>{fine.endDate}</TableCell>
                      <TableCell style={{ textAlign: 'center' }}>{fine.finePerDay}</TableCell>
                      <TableCell style={{ textAlign: 'center' }}>
                        <Tooltip title={authorization.uModify === 1 ? "" : "You are not authorized to edit"} arrow>
                          <span>
                            <Button
                              variant="contained"
                              color="warning"
                              spacing={2}
                              onClick={() => handleEdit(fine.dueDateId)}
                              startIcon={<EditIcon />}
                              disabled={authorization.uModify !== 1}
                            >
                              <b>EDIT</b>
                            </Button>
                          </span>
                        </Tooltip>
                        <Tooltip title={authorization.uDelete === 1 ? "" : "You are not authorized to delete"} arrow>
                          <span>
                            <Button
                              variant="contained"
                              color="error"
                              style={{ marginLeft: "10px" }}
                              onClick={() => handleShow(fine.dueDateId, fine.monthName)}
                              startIcon={<DeleteIcon />}
                              disabled={authorization.uDelete !== 1}
                            >
                              <b>DELETE</b>
                            </Button>
                          </span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      No Fine Details Found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>

            </Table>
            {filteredData.length > 0 && (
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 2 }}>
                <TextField
                  select
                  label="Rows per page"
                  value={rowsPerPage}
                  onChange={handleChangeRowsPerPage}
                  sx={{ width: 150 }}
                  SelectProps={{
                    native: true,
                  }}
                >
                  {rowsPerPageOptions.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </TextField>

                <Box>
                  <Typography variant="body2" sx={{ display: 'inline-block', marginRight: 2 }}>
                    Page {page} of {Math.ceil(filteredData.length / rowsPerPage)}
                  </Typography>
                  <Button
                    variant="outlined"
                    onClick={() => handleChangePage(null, page - 1)}
                    disabled={page === 1}
                    sx={{ mr: 1 }}
                  >
                    Prev
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() => handleChangePage(null, page + 1)}
                    disabled={page >= Math.ceil(filteredData.length / rowsPerPage)}
                  >
                    Next
                  </Button>
                </Box>
              </Box>
            )}

          </Grid>
        </Paper>
      </Grid>


      {/* ── Delete Confirmation Modal ── */}
      <Modal open={showModal} onClose={handleClose} aria-labelledby="delete-modal-title" aria-describedby="delete-modal-description">
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 400,
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            overflow: 'hidden',
          }}
        >
          {/* Header */}
          <Box sx={{ backgroundColor: '#0B1F40', padding: '12px 16px' }}>
            <Typography id="delete-modal-title" variant="h6" sx={{ color: '#fff' }}>
              Delete Confirmation ⚠️
            </Typography>
          </Box>

          {/* Content */}
          <Box sx={{ padding: '20px' }}>
            <Typography id="delete-modal-description" sx={{ mb: 2 }}>
              Are you sure you want to delete fine for <strong>{selectedFineDetails.monthName}</strong>?
            </Typography>

            {/* Buttons */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
              <Button onClick={handleClose} variant="outlined" sx={{ mr: 1 }}>
                Cancel
              </Button>
              <Button
                variant="contained"
                color="error"
                onClick={() => handleDelete(selectedFineDetails.dueDateId)}
              >
                Yes, Delete
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>

      {/* ── Info Modal ── */}
      <Modal
        open={showInfoModal}
        onClose={() => setShowInfoModal(false)}
        aria-labelledby="info-modal-title"
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: '95vw', sm: 620 },
            maxHeight: '85vh',
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* Info Modal Header */}
          <Box
            sx={{
              backgroundColor: '#0B1F3D',
              padding: '14px 20px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <InfoIcon sx={{ color: '#90CAF9', fontSize: 22 }} />
              <Typography id="info-modal-title" variant="h6" sx={{ color: '#fff', fontWeight: 'bold' }}>
                Fine Details — Page Information
              </Typography>
            </Box>
          
          </Box>

          {/* Info Modal Scrollable Body */}
          <Box sx={{ overflowY: 'auto', padding: '20px', flex: 1 }}>
            {infoSections.map((section, sIdx) => (
              <Box key={sIdx} sx={{ mb: 3 }}>
                {/* Section Title */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    mb: 1.5,
                  }}
                >
                  <Box
                    sx={{
                      width: 4,
                      height: 22,
                      borderRadius: 1,
                      backgroundColor: section.color,
                    }}
                  />
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: 'bold', color: section.color }}
                  >
                    {section.title}
                  </Typography>
                </Box>

                {/* Overview text (no fields) */}
                {section.description && (
                  <Typography variant="body2" sx={{ color: '#555', pl: 1.5 }}>
                    {section.description}
                  </Typography>
                )}

                {/* Field rows */}
                {section.fields && (
                  <Box
                    sx={{
                      border: '1px solid #e0e0e0',
                      borderRadius: 1,
                      overflow: 'hidden',
                    }}
                  >
                    {section.fields.map((field, fIdx) => (
                      <Box key={fIdx}>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: 2,
                            px: 2,
                            py: 1.2,
                            backgroundColor: fIdx % 2 === 0 ? '#fafafa' : '#fff',
                          }}
                        >
                          <Chip
                            label={field.label}
                            size="small"
                            sx={{
                              backgroundColor: section.color,
                              color: '#fff',
                              fontWeight: 'bold',
                              fontSize: '0.72rem',
                              minWidth: 120,
                              borderRadius: '4px',
                              flexShrink: 0,
                            }}
                          />
                          <Typography variant="body2" sx={{ color: '#444', lineHeight: 1.6 }}>
                            {field.desc}
                          </Typography>
                        </Box>
                        {fIdx < section.fields.length - 1 && <Divider />}
                      </Box>
                    ))}
                  </Box>
                )}

                {sIdx < infoSections.length - 1 && (
                  <Divider sx={{ mt: 2 }} />
                )}
              </Box>
            ))}
          </Box>

          {/* Info Modal Footer */}
          <Box
            sx={{
              borderTop: '1px solid #e0e0e0',
              px: 2.5,
              py: 1.5,
              display: 'flex',
              justifyContent: 'flex-end',
              backgroundColor: '#f9f9f9',
            }}
          >
            <Button
              variant="contained"
              onClick={() => setShowInfoModal(false)}
              sx={{ backgroundColor: '#0B1F3D', '&:hover': { backgroundColor: '#163060' } }}
            >
              Close
            </Button>
          </Box>
        </Box>
      </Modal>

      <Snackbar
        open={openSnackbar}
        autoHideDuration={4000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setOpenSnackbar(false)} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>

    </Container>
  );
}

export default DueDateDetails;